import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * The address typed on the login screen, kept in memory only for the code screen. It is never
 * put in a URL, router state, web storage or a log line. A page reload clears it and the
 * visitor starts again at /login (the challenge cookie is HttpOnly and not ours to read).
 */
interface LoginFlowValue {
  email: string | null;
  /** Epoch ms before which "send a new code" stays disabled. */
  cooldownUntil: number;
  /** True when the visitor chose "I already have a code": the code screen runs without an address. */
  codeOnly: boolean;
  start: (email: string, resendAfterSeconds: number) => void;
  setCooldown: (seconds: number) => void;
  /** Open the code screen without having requested a code in this page load (no address held, no request made). */
  startCodeOnly: () => void;
  clear: () => void;
}

const Ctx = createContext<LoginFlowValue | null>(null);

export function LoginFlowProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [codeOnly, setCodeOnly] = useState(false);
  const value = useMemo<LoginFlowValue>(
    () => ({
      email,
      cooldownUntil,
      codeOnly,
      start: (e, s) => {
        setCodeOnly(false);
        setEmail(e);
        setCooldownUntil(Date.now() + s * 1000);
      },
      setCooldown: (s) => setCooldownUntil(Date.now() + s * 1000),
      startCodeOnly: () => {
        setEmail(null);
        setCooldownUntil(0);
        setCodeOnly(true);
      },
      clear: () => {
        setCodeOnly(false);
        setEmail(null);
        setCooldownUntil(0);
      },
    }),
    [email, cooldownUntil, codeOnly],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLoginFlow(): LoginFlowValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useLoginFlow must be used inside <LoginFlowProvider>');
  return v;
}
